import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { doc, updateDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useUser } from '../context/UserContext';
import { IoBookmark, IoBookmarkOutline, IoTrash, IoClose } from 'react-icons/io5';
import './Bookmarks.css';

export default function Bookmarks({ bookId, chapterId, chapterTitle }) {
  const { user } = useUser();
  const [bookmarks, setBookmarks] = useState([]);
  const [showList, setShowList] = useState(false);
  const [showInput, setShowInput] = useState(false);
  const [note, setNote] = useState('');

  const docPath = `userLibraries/${user.id}`;

  useEffect(() => {
    const unsub = onSnapshot(doc(db, docPath), (snapshot) => {
      if (snapshot.exists()) {
        setBookmarks(snapshot.data().bookmarks || []);
      }
    });
    return () => unsub();
  }, [docPath]);

  const myBookmarks = bookmarks.filter(b => b.bookId === bookId);

  const addBookmark = async () => {
    const newBookmark = {
      id: Date.now().toString(),
      bookId,
      chapterId,
      chapterTitle,
      note: note.trim(),
      createdAt: new Date().toISOString(),
    };
    const updated = [...bookmarks, newBookmark];
    await updateDoc(doc(db, docPath), { bookmarks: updated });
    setNote('');
    setShowInput(false);
  };

  const removeBookmark = async (bmId) => {
    const updated = bookmarks.filter(b => b.id !== bmId);
    await updateDoc(doc(db, docPath), { bookmarks: updated });
  };

  return (
    <div className="bookmarks-container">
      <div className="bookmarks-actions">
        <motion.button
          className="reader-action"
          onClick={() => setShowInput(!showInput)}
          whileTap={{ scale: 0.9 }}
          title="Ajouter un signet"
        >
          <IoBookmarkOutline />
        </motion.button>

        {myBookmarks.length > 0 && (
          <motion.button
            className="bookmarks-count"
            onClick={() => setShowList(!showList)}
            whileTap={{ scale: 0.9 }}
          >
            {myBookmarks.length}
          </motion.button>
        )}
      </div>

      {/* Ajouter un signet */}
      <AnimatePresence>
        {showInput && (
          <motion.div
            className="bookmark-add-panel"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <input
              className="input bookmark-note-input"
              placeholder="Note (optionnel)..."
              value={note}
              onChange={e => setNote(e.target.value)}
              maxLength={200}
            />
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary" style={{ flex: 1, fontSize: 13 }} onClick={addBookmark}>
                <IoBookmark /> Marquer ici
              </button>
              <button className="btn btn-ghost" onClick={() => setShowInput(false)}><IoClose /></button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Liste des signets */}
      <AnimatePresence>
        {showList && (
          <>
            <motion.div
              className="bookmark-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowList(false)}
            />
            <motion.div
              className="bookmark-list-panel"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
            >
              <div className="bookmark-list-header">
                <h3>Mes signets ({myBookmarks.length})</h3>
                <button onClick={() => setShowList(false)}><IoClose /></button>
              </div>
              {myBookmarks.map((bm, i) => (
                <motion.div
                  key={bm.id}
                  className="bookmark-item"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                >
                  <IoBookmark className="bookmark-item-icon" />
                  <div className="bookmark-item-info">
                    <p className="bookmark-item-chapter">{bm.chapterTitle}</p>
                    {bm.note && <p className="bookmark-item-note">{bm.note}</p>}
                  </div>
                  <motion.button
                    className="bookmark-item-remove"
                    onClick={() => removeBookmark(bm.id)}
                    whileTap={{ scale: 0.8 }}
                  >
                    <IoTrash />
                  </motion.button>
                </motion.div>
              ))}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}